/**
 * Sprint 006 (A1) — Canonical text/surface token vocabulary.
 *
 * The app had drifted into two palette generations (slate-* vs white/x).
 * These constants pin the ratified white/x canon so new code has one
 * obvious spelling per role. They are plain class strings, not a theme
 * system — Tailwind still sees literal classes at every call site that
 * uses them, and grep stays trivial.
 *
 * Mapping from the retired slate palette:
 *   text-slate-400 → TEXT.tertiary   (white/50)
 *   text-slate-300 → TEXT.secondary  (white/70)
 *   text-slate-200 → TEXT.strong     (white/80)
 *   bg-slate-700/40 chips → SURFACE.chip
 *   bg-slate-900 popovers → SURFACE.solid (deliberately opaque, not glass)
 */

export const TEXT = {
  /** Primary content, headings, values */
  primary: 'text-white',
  /** Emphasized body copy */
  strong: 'text-white/80',
  /** Regular body copy, table cells */
  secondary: 'text-white/70',
  /** Supporting copy, subtitles, counts */
  tertiary: 'text-white/50',
  /** De-emphasized: placeholders, icons at rest, table headers */
  muted: 'text-white/40',
  /** Barely-there: fine print, decorative */
  faint: 'text-white/30',
} as const;

export const SURFACE = {
  /** Chip / small control at rest */
  chip: 'bg-white/5 border border-white/10',
  /** Chip / small control, active state */
  chipActive: 'bg-white/10 border border-white/20',
  /**
   * Opaque overlay surface (popovers, dropdown menus, context menus).
   * Deliberately solid instead of translucent glass — floating layers
   * over table text need full contrast. One spelling, everywhere.
   */
  solid: 'bg-zinc-900 border border-white/10 shadow-xl shadow-black/40',
} as const;

/** Page-level h1 — one heading style across all routes. */
export const PAGE_TITLE = 'text-2xl font-semibold text-white';

/**
 * Sprint 006b (P5-Kanon) — Tabellen-Header-Vokabular.
 *
 * Mehrheits-Kanon aus suppliers/expenses (px-4 py-3) + InvoiceListView
 * (text-xs uppercase tracking-wider): eine th-Anatomie für alle Tabellen.
 * expenses.tsx behält seinen responsiven `px-2 sm:px-4`-Dichte-Präfix
 * (mobile-first Tabelle) — Farbe/Größe/Tracking kommen trotzdem von hier.
 */
export const TABLE = {
  /** Standard-Spaltenkopf (links ausgerichtet) */
  th: 'px-4 py-3 text-left text-xs font-medium text-white/40 uppercase tracking-wider',
  /** Zahlen-/Geldspaltenkopf (rechtsbündig, tabular) */
  thNumeric:
    'px-4 py-3 text-right text-xs font-medium text-white/40 uppercase tracking-wider tabular-nums',
} as const;
